import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userKycAPI, authAPI } from '../../services/api';

const FIELD_STYLE = {
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#4a4a6a', marginBottom: 6 },
  input: {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e8e8f1', borderRadius: 8,
    fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#14142b',
    background: '#fff', transition: 'border .2s',
  },
  error: { color: '#DC2626', fontSize: 12, marginTop: 4 },
  fileBox: {
    border: '1.5px dashed #e8e8f1', borderRadius: 8, padding: '14px 12px',
    cursor: 'pointer', background: '#f9f9fc', textAlign: 'center', fontSize: 13, color: '#6e6e86',
  },
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #E5E7EB' }}>{title}</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>{children}</div>
  </div>
);

const Field = ({ label, error, children, full }) => (
  <div style={{ gridColumn: full ? '1/-1' : undefined }}>
    <label style={FIELD_STYLE.label}>{label}</label>
    {children}
    {error && <div style={FIELD_STYLE.error}>{error}</div>}
  </div>
);

const FileInput = ({ name, label, accept, onChange, value, error }) => {
  const ref = useRef();
  return (
    <Field label={label} error={error}>
      <div style={{ ...FIELD_STYLE.fileBox, borderColor: error ? '#DC2626' : value ? '#10B981' : '#e8e8f1' }}
        onClick={() => ref.current.click()}>
        {value ? <span style={{ color: '#10B981', fontWeight: 600 }}>✓ {value.name}</span> : <span>Click to upload (JPG, PNG, PDF · max 5MB)</span>}
      </div>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => onChange(name, e.target.files[0])} />
    </Field>
  );
};

export default function UserKYC() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [text, setText] = useState({
    full_name: '', mobile: user?.phone || '', address: '',
    account_holder_name: '', account_number: '', ifsc_code: '', website_url: '',
  });
  const [files, setFiles] = useState({
    aadhaar_front: null, aadhaar_back: null, pan_card: null,
    selfie: null, cancelled_cheque: null, gst_certificate: null,
  });
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setFile = (name, file) => {
    setFiles(p => ({ ...p, [name]: file }));
    if (errors[name]) setErrors(p => { const n = { ...p }; delete n[name]; return n; });
  };

  const handleText = (e) => {
    const { name, value } = e.target;
    setText(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => { const n = { ...p }; delete n[name]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!text.full_name)           e.full_name           = 'Full name is required';
    if (!text.mobile)              e.mobile              = 'Mobile is required';
    if (!text.address)             e.address             = 'Address is required';
    if (!files.aadhaar_front)      e.aadhaar_front       = 'Aadhaar front is required';
    if (!files.aadhaar_back)       e.aadhaar_back        = 'Aadhaar back is required';
    if (!files.pan_card)           e.pan_card            = 'PAN card is required';
    if (!files.selfie)             e.selfie              = 'Selfie is required';
    if (!files.cancelled_cheque)   e.cancelled_cheque    = 'Cancelled cheque is required';
    if (!text.account_holder_name) e.account_holder_name = 'Account holder name is required';
    if (!text.account_number)      e.account_number      = 'Account number is required';
    if (!text.ifsc_code)           e.ifsc_code           = 'IFSC code is required';
    if (!terms)                    e.terms_acceptance    = 'You must accept the terms';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(text).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append('terms_acceptance', '1');

      await userKycAPI.submit(fd);
      setSubmitted(true);
      setTimeout(() => navigate('/user/pending', { replace: true }), 2000);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        const mapped = {};
        Object.entries(data.errors).forEach(([k, v]) => { mapped[k] = Array.isArray(v) ? v[0] : v; });
        setErrors(mapped);
      } else {
        setErrors({ _global: data?.message || 'Submission failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (_) {}
    logout();
    navigate('/login', { replace: true });
  };

  if (submitted) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
          <h2 style={{ fontSize: 22, color: '#065F46', margin: '0 0 8px' }}>KYC Submitted!</h2>
          <p style={{ color: '#6e6e86' }}>Redirecting to pending screen…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.header}>
          <div style={S.brand}>A1xPay</div>
          <h1 style={S.title}>Complete your KYC</h1>
          <p style={S.sub}>Fill in your details to activate your account. Our team will review within 24–48 hours.</p>
        </div>

        <form onSubmit={handleSubmit} encType="multipart/form-data">
          {errors._global && <div style={S.globalErr}>{errors._global}</div>}

          <Section title="Personal Details">
            <Field label="Full Name *" error={errors.full_name}>
              <input name="full_name" value={text.full_name} onChange={handleText}
                placeholder="As on Aadhaar / PAN" style={FIELD_STYLE.input} />
            </Field>
            <Field label="Mobile Number *" error={errors.mobile}>
              <input name="mobile" value={text.mobile} onChange={handleText}
                placeholder="10-digit mobile" style={FIELD_STYLE.input} />
            </Field>
            <Field label="Current Address *" error={errors.address} full>
              <textarea name="address" value={text.address} onChange={handleText}
                placeholder="Full residential address" rows={3}
                style={{ ...FIELD_STYLE.input, resize: 'vertical' }} />
            </Field>
          </Section>

          <Section title="Document KYC">
            <FileInput name="aadhaar_front" label="Aadhaar Card (Front) *"
              accept=".jpg,.jpeg,.png,.pdf" onChange={setFile} value={files.aadhaar_front} error={errors.aadhaar_front} />
            <FileInput name="aadhaar_back" label="Aadhaar Card (Back) *"
              accept=".jpg,.jpeg,.png,.pdf" onChange={setFile} value={files.aadhaar_back} error={errors.aadhaar_back} />
            <FileInput name="pan_card" label="PAN Card *"
              accept=".jpg,.jpeg,.png,.pdf" onChange={setFile} value={files.pan_card} error={errors.pan_card} />
            <FileInput name="selfie" label="Selfie / Live Photo *"
              accept=".jpg,.jpeg,.png" onChange={setFile} value={files.selfie} error={errors.selfie} />
          </Section>

          <Section title="Bank Verification">
            <Field label="Account Holder Name *" error={errors.account_holder_name}>
              <input name="account_holder_name" value={text.account_holder_name} onChange={handleText}
                placeholder="As on bank account" style={FIELD_STYLE.input} />
            </Field>
            <Field label="Account Number *" error={errors.account_number}>
              <input name="account_number" value={text.account_number} onChange={handleText}
                placeholder="Bank account number" style={FIELD_STYLE.input} />
            </Field>
            <Field label="IFSC Code *" error={errors.ifsc_code}>
              <input name="ifsc_code" value={text.ifsc_code} onChange={handleText}
                placeholder="e.g. SBIN0001234" style={FIELD_STYLE.input} />
            </Field>
            <FileInput name="cancelled_cheque" label="Cancelled Cheque *"
              accept=".jpg,.jpeg,.png,.pdf" onChange={setFile} value={files.cancelled_cheque} error={errors.cancelled_cheque} />
          </Section>

          <Section title="Business Details (Optional)">
            <Field label="Website URL" error={errors.website_url}>
              <input name="website_url" value={text.website_url} onChange={handleText}
                placeholder="https://yourwebsite.com" style={FIELD_STYLE.input} />
            </Field>
            <FileInput name="gst_certificate" label="GST Certificate (Optional)"
              accept=".jpg,.jpeg,.png,.pdf" onChange={setFile} value={files.gst_certificate} error={errors.gst_certificate} />
          </Section>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: '#4a4a6a' }}>
              <input type="checkbox" checked={terms} onChange={e => { setTerms(e.target.checked); if (errors.terms_acceptance) setErrors(p => { const n = {...p}; delete n.terms_acceptance; return n; }); }}
                style={{ marginTop: 2, width: 16, height: 16, accentColor: '#14142b' }} />
              <span>I confirm that all the information and documents provided are genuine and accurate. I accept the <a href="/terms-conditions" target="_blank" style={{ color: '#14142b', fontWeight: 600 }}>Terms & Conditions</a>.</span>
            </label>
            {errors.terms_acceptance && <div style={{ ...FIELD_STYLE.error, marginTop: 6 }}>{errors.terms_acceptance}</div>}
          </div>

          <button type="submit" disabled={loading} style={{ ...S.btn, opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Submitting…' : 'Submit KYC'}
          </button>
        </form>

        <div style={S.footer}>
          <button onClick={handleLogout} style={S.logoutBtn}>Sign out</button>
          {user?.email && <span style={{ fontSize: 12, color: '#9CA3AF' }}>Signed in as {user.email}</span>}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', background: 'linear-gradient(135deg, #14142b 0%, #1E293B 100%)',
    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    padding: '40px 16px', fontFamily: '"Plus Jakarta Sans", system-ui, Arial, sans-serif',
  },
  card: {
    width: '100%', maxWidth: 760, background: '#fff', borderRadius: 16,
    padding: '36px 40px', boxShadow: '0 20px 60px rgba(0,0,0,.3)',
  },
  header: { marginBottom: 32, textAlign: 'center' },
  brand: { fontSize: 22, fontWeight: 800, color: '#7c3aed', marginBottom: 8 },
  title: { fontSize: 26, fontWeight: 700, color: '#14142b', margin: '0 0 8px' },
  sub:   { fontSize: 14, color: '#6e6e86', margin: 0 },
  globalErr: {
    background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
    padding: '12px 16px', color: '#DC2626', fontSize: 13, marginBottom: 20,
  },
  btn: {
    width: '100%', padding: '13px', background: '#7c3aed', color: '#fff',
    border: 0, borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
    marginBottom: 20,
  },
  footer: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginTop: 8 },
  logoutBtn: {
    background: 'none', border: '1px solid #e8e8f1', borderRadius: 8,
    padding: '8px 20px', fontSize: 13, color: '#6e6e86', cursor: 'pointer',
  },
};
